/* eslint-disable react/jsx-props-no-spreading */
import { ThemeProvider, createTheme } from "@mui/material";
import { AWSCloudWatchProvider, Amplify, Logger, Storage } from "aws-amplify";
import MainLayout from "components/main-layout";
import NoAuthLayout from "components/noauth-layout";
import Spinner from "components/spinner";
import CustomProvider from "hook/context";
import PageNotFound from "pages/page-not-found";
import { ReactElement, Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import { noauthRoutes, routes, securityRoutes } from "routes";
import { PrivateRoutes, UnLoggedRoutes } from "routes/private-routes";
import awsconfig from "./aws-exports";
import { ROUTERS } from "constants/routes.constant";
import MailNotFound from "pages/mail-not-found";

Amplify.configure(awsconfig);
Storage.configure({
  customPrefix: {
    public: "",
    protected: "protected/",
    private: "private/",
  },
});
const logger = new Logger("SUMMIT_HSHC", "DEBUG");
Amplify.register(logger);
logger.addPluggable(
  new AWSCloudWatchProvider({
    logGroupName: "FRONTEND",
    logStreamName: process.env.ENV ?? "production",
    region: awsconfig.aws_project_region,
  }),
);
const theme = createTheme({
  typography: {
    fontFamily: '"Roboto", sans-serif',
  },

  zIndex: {
    appBar: 1000,
    drawer: 800,
  },
});
const Service = lazy(() => import("./pages/service-list"));

const App = (): ReactElement => {
  return (
    <ThemeProvider theme={theme}>
      <CustomProvider>
        <Routes>
          <Route element={<PrivateRoutes />}>
            <Route path="/" element={<MainLayout />}>
              <Route
                index
                element={
                  <Suspense fallback={<Spinner />}>
                    <Service />
                  </Suspense>
                }
              />
              {routes.map(({ path, component, ...rest }, index) => (
                <Route
                  key={`route_${index}`}
                  path={path}
                  element={
                    <Suspense fallback={<Spinner />}>{component}</Suspense>
                  }
                  {...rest}
                />
              ))}
            </Route>
          </Route>
          <Route element={<UnLoggedRoutes />}>
            <Route path="/" element={<NoAuthLayout />}>
              {noauthRoutes.map(({ path, component, ...rest }, index) => (
                <Route
                  key={`noauth_route_${index}`}
                  path={path}
                  element={
                    <Suspense fallback={<Spinner isFullWidth={true} />}>
                      {component}
                    </Suspense>
                  }
                  {...rest}
                />
              ))}
            </Route>
          </Route>
          <Route path="/" element={<NoAuthLayout />}>
            {securityRoutes.map(({ path, component, ...rest }, index) => (
              <Route
                key={`security_route_${index}`}
                path={path}
                element={
                  <Suspense fallback={<Spinner isFullWidth={true} />}>
                    {component}
                  </Suspense>
                }
                {...rest}
              />
            ))}
          </Route>
          <Route path={ROUTERS.MAIL_NOT_FOUND} element={<MailNotFound />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </CustomProvider>
    </ThemeProvider>
  );
};

export default App;
export { logger };
